/*
 * The photo gallery (prototype: .vFlow .o1Photos and the .pv viewer, "Photos by count", Gabriel 2026-10-19).
 * Colors are theme tokens only, except the viewer's ink and its white controls: the prototype draws the viewer
 * theme-neutral (near-black backdrop, white controls) in every look, as the Mini App's lightbox is, so those two
 * channels are set once here and every color below is rgba() over them.
 */
@layer telify {
  /* The viewer and the "+N more" scrim stay near-black with white controls in every look, like the Mini App lightbox. */
  .photos,
  .viewer {
    --pv-ink-rgb: 8, 9, 11;
    --pv-on-rgb: 255, 255, 255;
  }

  .photos {
    display: grid;
    gap: 8px;
  }

  /* One to three extra photos sit at one fixed size, left aligned and never stretched. */
  .thumbs {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .shot {
    appearance: none;
    border: 0;
    padding: 0;
    cursor: zoom-in;
    background: var(--bg-card-2);
    overflow: hidden;
    position: relative;
    display: block;
    width: 100%;
  }

  /* The framed photo (ui/Photo) fills the button; the hover zoom moves the whole frame. */
  .shotImg {
    transition: transform 0.5s ease;
  }

  .shot:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  .main {
    height: 400px;
    border-radius: 16px;
  }

  .thumb {
    height: 150px;
    border-radius: 10px;
  }

  .more {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    background: rgba(var(--pv-ink-rgb), 0.55);
    color: rgb(var(--pv-on-rgb));
    font-size: 17px;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  /* The viewer: the app's DetailLightbox as a window over everything. */
  .viewer {
    position: fixed;
    inset: 0;
    z-index: 130;
    animation: viewerIn 0.22s ease backwards;
  }

  .back {
    position: absolute;
    inset: 0;
    background: rgba(var(--pv-ink-rgb), 0.92);
  }

  .stage {
    position: absolute;
    inset: 72px 104px 64px;
    display: grid;
    place-items: center;
    touch-action: pan-y;
  }

  .stage img {
    max-width: 100%;
    max-height: calc(100vh - 136px);
    object-fit: contain;
    border-radius: 6px;
    box-shadow: 0 24px 60px rgba(var(--pv-ink-rgb), 0.5);
    user-select: none;
    -webkit-user-drag: none;
    animation: photoIn 0.32s cubic-bezier(0.2, 0.7, 0.2, 1) backwards;
    transition: transform 0.36s cubic-bezier(0.2, 0.9, 0.3, 1.15);
  }

  .stage.dragging img {
    transition: none;
  }

  .btn {
    appearance: none;
    cursor: pointer;
    position: absolute;
    z-index: 2;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    border: 0;
    background: rgba(var(--pv-on-rgb), 0.12);
    color: rgb(var(--pv-on-rgb));
    transition: background 0.15s ease;
  }

  .btn:focus-visible {
    outline: 2px solid rgb(var(--pv-on-rgb));
    outline-offset: 2px;
  }

  .btn svg {
    width: 22px;
    height: 22px;
    stroke-width: 2.2;
  }

  .prev {
    left: 28px;
    top: 50%;
    margin-top: -26px;
  }

  .next {
    right: 28px;
    top: 50%;
    margin-top: -26px;
  }

  .close {
    right: 24px;
    top: 16px;
    width: 44px;
    height: 44px;
  }

  .count {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 22px;
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.04em;
    color: rgba(var(--pv-on-rgb), 0.82);
    font-variant-numeric: tabular-nums;
  }

  @keyframes viewerIn {
    from {
      opacity: 0;
    }
  }

  @keyframes photoIn {
    from {
      opacity: 0;
      transform: scale(0.98);
    }
  }

  @media (hover: hover) {
    .shot:hover .shotImg {
      transform: scale(1.03);
    }

    .btn:hover {
      background: rgba(var(--pv-on-rgb), 0.22);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .viewer,
    .stage img {
      animation: none;
    }
  }

  /* Phone width: full screen, a swipe, no arrow buttons. */
  @media (max-width: 1023px) {
    .stage {
      inset: 64px 0 56px;
    }

    .stage img {
      border-radius: 0;
      max-height: calc(100vh - 120px);
    }

    .prev,
    .next {
      display: none;
    }

    .close {
      right: 12px;
      top: 10px;
    }
  }

  /* web-studio-sites D12: a custom site's narrow drawing (bookings.html .o1Photos at 860 pixels). */
  @media (max-width: 860px) {
    :global(:root[data-custom-site]) .main {
      height: 300px;
    }

    :global(:root[data-custom-site]) .thumb {
      height: 96px;
    }
  }
}
